// Note for PR2: `features/auth/domain/types` also has an `AuthSession` (the login-response
// mapper shape) — distinct on purpose, but PR2 needs to bridge one into the other.
export interface AuthSession {
	accessToken: string | null;
	refreshToken: string | null;
	idToken: string | null;
	isSuperUser: boolean;
	isStaff: boolean;
	defaultCompanyId: number | null;
	delegationsIds: number[] | null;
}

export const DELEGATION_SCOPE = {
	ALL: 'all',
	SELECTED: 'selected',
} as const;

export type DelegationScopeKind = (typeof DELEGATION_SCOPE)[keyof typeof DELEGATION_SCOPE];
export type ScopeKey = `v1:c=${number};d=${string}`;

export interface AuthScope {
	companyId: number;
	delegationKind: DelegationScopeKind;
	delegationIds: readonly number[];
}

export interface ScopedCredential {
	accessToken: string;
	refreshToken: string;
	idToken: string | null;
}

export interface CredentialEntry extends ScopedCredential {
	scope: AuthScope;
	revision: number;
	acquiredAt: number;
	lastUsedAt: number;
	expiresAt: number;
}

export interface PersistedCredentialEntry extends Omit<CredentialEntry, 'idToken' | 'scope'> {
	companyId: number;
	delegationIds: readonly number[];
}

export interface PersistedAuthSessionV2 {
	version: 2;
	loginEpoch: number;
	nextRevision: number;
	isSuperUser: boolean;
	isStaff: boolean;
	activeScopeKey: ScopeKey | null;
	lastUsableScopeKey: ScopeKey | null;
	entries: Record<ScopeKey, PersistedCredentialEntry>;
}

export interface AcquisitionIntent {
	targetScopeKey: ScopeKey;
	intentRevision: number;
	loginEpoch: number;
}

export interface RequestCredentialSnapshot {
	scopeKey: ScopeKey;
	entryRevision: number;
	accessToken: string;
}

export interface SessionContextValue {
	isAuthenticated: boolean;
	isSuperUser: boolean;
	isStaff: boolean;
	accessToken: string | null;
}
